import React, { useEffect, useState } from "react";
import { employees, getEmployeeName } from "../../Config/Employees";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";

export const EmployeeSalary = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const user = localStorage.getItem("logged_in_email") || "";
  const { isAdmin } = getUserAccess();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [salaryRecords, setSalaryRecords] = useState<any[]>([]);
  const [currentSalary, setCurrentSalary] = useState<any>(null);

  // Filters
  const [filterEmail, setFilterEmail] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Create salary modal (admin)
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const [newSalary, setNewSalary] = useState({
    email: "",
    month: "",
    year: "",
    baseSalary: "",
    salaryDueDate: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update status modal (admin)
  const {
    isOpen: isUpdateOpen,
    onOpen: onUpdateOpen,
    onClose: onUpdateClose,
  } = useDisclosure();
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [updateData, setUpdateData] = useState({
    status: "",
    holdReason: "",
  });

  // Add deduction modal (admin)
  const {
    isOpen: isDeductionOpen,
    onOpen: onDeductionOpen,
    onClose: onDeductionClose,
  } = useDisclosure();
  const [deduction, setDeduction] = useState({ reason: "", amount: "" });


  const currentMonth = DateTime.now().toFormat("MM");
  const currentYear = DateTime.now().year.toString();

  const generateMonthYearArray = () => {
    const startDate = DateTime.fromObject({ month: 11, year: 2025 });
    const endDate = DateTime.now();
    let currentDate = startDate;
    const arr: any[] = [];
    while (currentDate <= endDate) {
      arr.push({
        month: currentDate.toFormat("MM"),
        year: currentDate.year.toString(),
        text: `${currentDate.toFormat("MMMM")}-${currentDate.year}`,
      });
      currentDate = currentDate.plus({ months: 1 });
    }
    return arr;
  };

  const getStatusBadge = (status: string) => {
    const colorMap: any = {
      paid: "green",
      pending: "orange",
      on_hold: "red",
    };
    return (
      <Badge colorScheme={colorMap[status] || "gray"}>
        {status?.replace(/_/g, " ")}
      </Badge>
    );
  };

  const fetchSalary = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getSalary(
        token,
        user,
        isAdmin ? filterEmail : "",
        filterMonth,
        filterYear
      );
      if (res.data?.data) {
        setSalaryRecords(res.data.data);
        if (!isAdmin) {
          const current = res.data.data.find(
            (s: any) =>
              s.month === currentMonth && s.year?.toString() === currentYear
          );
          setCurrentSalary(current || null);
        }
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSalary = async () => {
    if (!newSalary.email || !newSalary.month || !newSalary.year || !newSalary.baseSalary) {
      toast({
        position: "top",
        title: "Please fill all required fields",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await EmployeeDataService.createSalary(token, { ...newSalary, name: getEmployeeName(newSalary.email) }, user);
      toast({
        position: "top",
        title: "Salary record created",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onCreateClose();
      setNewSalary({ email: "", month: "", year: "", baseSalary: "", salaryDueDate: "" });
      fetchSalary();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Failed to create salary record",
        description: e.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedRecord) return;
    try {
      await EmployeeDataService.updateSalary(
        token,
        selectedRecord._id,
        {
          status: updateData.status,
          holdReason: updateData.status === "on_hold" ? updateData.holdReason : "",
        },
        user
      );
      toast({
        position: "top",
        title: "Salary status updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onUpdateClose();
      fetchSalary();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Update failed",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleAddDeduction = async () => {
    if (!selectedRecord || !deduction.reason || !deduction.amount) {
      toast({
        position: "top",
        title: "Please fill reason and amount",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    try {
      await EmployeeDataService.addSalaryDeduction(
        token,
        selectedRecord._id,
        { reason: deduction.reason, amount: Number(deduction.amount) },
        user
      );
      toast({
        position: "top",
        title: "Deduction added",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onDeductionClose();
      setDeduction({ reason: "", amount: "" });
      fetchSalary();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Failed to add deduction",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const getDeductionsTotal = (deductions: any[]) => {
    if (!deductions || !Array.isArray(deductions)) return 0;
    return deductions.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);
  };

  useEffect(() => {
    fetchSalary();
  }, []);

  const filteredRecords = filterStatus
    ? salaryRecords.filter((s: any) => s.status === filterStatus)
    : salaryRecords;

  return (
    <HrmsPageShell title="Salary" isLoading={isLoading}>
      <>
        {/* Warning */}
        <Box bg="orange.50" border="1px" borderColor="orange.200" borderRadius="md" p={3} mb={6}>
          <Text color="orange.700" fontSize="sm" fontWeight="semibold" m={0}>
            Salary will be held if monthly confirmation is not submitted.
          </Text>
        </Box>

        {/* Filters */}
        <div className="top_section_container">
          <div className="top_section_lower_container">
            <div
              className="nested p-lg-0"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              {isAdmin && (
                <div className="input-group">
                  <div className="input-group-append">
                    <Button
                      variant="outline"
                      bgColor="#327171"
                      color="white"
                      onClick={onCreateOpen}
                    >
                      Create Salary Record
                    </Button>
                  </div>
                </div>
              )}

              <Flex p={4} alignItems="start" justifyContent="end" gap={8}>
                {isAdmin && (
                  <Flex display="flex" alignItems="flex-end" w="250px">
                    <Select
                      placeholder="Select Employee"
                      onChange={(e) => setFilterEmail(e.target.value)}
                      value={filterEmail}
                      color="#327171"
                      borderRightRadius={0}
                      focusBorderColor="teal"
                      borderWidth="0.2px"
                      _focus={{
                        borderWidth: "0.2px",
                        borderColor: "teal.200",
                      }}
                    >
                      {employees.map((emp) => (
                        <option key={emp.value} value={emp.value}>
                          {emp.label}
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="md"
                      borderRadius="md"
                      borderLeftRadius={0}
                      p={5}
                      colorScheme="whiteAlpha"
                      bg="#327171"
                      onClick={() => fetchSalary()}
                    >
                      Search
                    </Button>
                  </Flex>
                )}

                <Flex display="flex" alignItems="flex-end" w="250px">
                  <Select
                    placeholder="Select Month"
                    onChange={(e) => {
                      if (e.target.value) {
                        const [m, y] = e.target.value.split("-");
                        setFilterMonth(m);
                        setFilterYear(y);
                      } else {
                        setFilterMonth("");
                        setFilterYear("");
                      }
                    }}
                    color="#327171"
                    borderRightRadius={0}
                    focusBorderColor="teal"
                    borderWidth="0.2px"
                    _focus={{
                      borderWidth: "0.2px",
                      borderColor: "teal.200",
                    }}
                  >
                    {generateMonthYearArray().map((el) => (
                      <option key={`${el.month}-${el.year}`} value={`${el.month}-${el.year}`}>
                        {el.text}
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="md"
                    borderRadius="md"
                    borderLeftRadius={0}
                    p={5}
                    colorScheme="whiteAlpha"
                    bg="#327171"
                    onClick={() => fetchSalary()}
                  >
                    Search
                  </Button>
                </Flex>

                {isAdmin && (
                  <Flex display="flex" alignItems="flex-end" w="200px">
                    <Select
                      placeholder="All Statuses"
                      onChange={(e) => setFilterStatus(e.target.value)}
                      value={filterStatus}
                      color="#327171"
                      borderRightRadius={0}
                      focusBorderColor="teal"
                      borderWidth="0.2px"
                      _focus={{
                        borderWidth: "0.2px",
                        borderColor: "teal.200",
                      }}
                    >
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="on_hold">On Hold</option>
                    </Select>
                    <Button
                      size="md"
                      borderRadius="md"
                      borderLeftRadius={0}
                      p={5}
                      colorScheme="whiteAlpha"
                      bg="#327171"
                      onClick={() => fetchSalary()}
                    >
                      Search
                    </Button>
                  </Flex>
                )}
              </Flex>
            </div>
          </div>
        </div>

        {/* Employee Current Month Cards */}
        {!isAdmin && currentSalary && (
          <Box my={8}>
            <SimpleGrid columns={{ base: 2, md: 6 }} spacing={10}>
              <Box p={2} borderWidth="1px" borderRadius="lg" boxShadow="sm" textAlign="center" bg="white">
                <Text fontSize="sm" color="gray.500" fontWeight="semibold" m={0}>Base Salary</Text>
                <Text fontSize="lg" fontWeight="bold" color="teal.600" m={0}>
                  {currentSalary.baseSalary || "-"}
                </Text>
              </Box>
              <Box p={2} borderWidth="1px" borderRadius="lg" boxShadow="sm" textAlign="center" bg="white">
                <Text fontSize="sm" color="gray.500" fontWeight="semibold" m={0}>Deductions</Text>
                <Text fontSize="lg" fontWeight="bold" color="red.500" m={0}>
                  {getDeductionsTotal(currentSalary.deductions)}
                </Text>
              </Box>
              <Box p={2} borderWidth="1px" borderRadius="lg" boxShadow="sm" textAlign="center" bg="white">
                <Text fontSize="sm" color="gray.500" fontWeight="semibold" m={0}>Net Salary</Text>
                <Text fontSize="lg" fontWeight="bold" color="teal.600" m={0}>
                  {currentSalary.netSalary || (currentSalary.baseSalary - getDeductionsTotal(currentSalary.deductions))}
                </Text>
              </Box>
              <Box p={2} borderWidth="1px" borderRadius="lg" boxShadow="sm" textAlign="center" bg="white">
                <Text fontSize="sm" color="gray.500" fontWeight="semibold" m={0}>Status</Text>
                <Text fontSize="lg" fontWeight="bold" m={0}>
                  {getStatusBadge(currentSalary.status || "pending")}
                </Text>
              </Box>
              <Box p={2} borderWidth="1px" borderRadius="lg" boxShadow="sm" textAlign="center" bg="white">
                <Text fontSize="sm" color="gray.500" fontWeight="semibold" m={0}>Due Date</Text>
                <Text fontSize="lg" fontWeight="bold" color="teal.600" m={0}>
                  {currentSalary.salaryDueDate
                    ? DateTime.fromISO(currentSalary.salaryDueDate).toFormat("dd MMM yyyy")
                    : "-"}
                </Text>
              </Box>
              {currentSalary.status === "on_hold" && currentSalary.holdReason && (
                <Box p={2} borderWidth="1px" borderRadius="lg" boxShadow="sm" textAlign="center" bg="white">
                  <Text fontSize="sm" color="gray.500" fontWeight="semibold" m={0}>Hold Reason</Text>
                  <Text fontSize="lg" fontWeight="bold" color="red.500" m={0}>
                    {currentSalary.holdReason}
                  </Text>
                </Box>
              )}
            </SimpleGrid>
          </Box>
        )}

        {/* Main section */}
        <div className="main-section" style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Month</th>
                <th>Year</th>
                <th>Base Salary</th>
                <th>Deductions</th>
                <th>Net Salary</th>
                <th>Status</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? (
                filteredRecords.map((s: any) => (
                  <tr key={s._id}>
                    <td>{s.name || "-"}</td>
                    <td>{s.email}</td>
                    <td>{DateTime.fromObject({ month: parseInt(s.month) }).toFormat("MMMM")}</td>
                    <td>{s.year}</td>
                    <td>{s.baseSalary}</td>
                    <td>{getDeductionsTotal(s.deductions)}</td>
                    <td>{s.netSalary || (s.baseSalary - getDeductionsTotal(s.deductions))}</td>
                    <td>
                      {getStatusBadge(s.status || "pending")}
                      {s.status === "on_hold" && s.holdReason && (
                        <Text fontSize="xs" color="red.500" m={0}>{s.holdReason}</Text>
                      )}
                    </td>
                    {isAdmin && (
                      <td>
                        <Flex gap={2}>
                          <Button
                            size="xs"
                            colorScheme="teal"
                            onClick={() => {
                              setSelectedRecord(s);
                              setUpdateData({ status: s.status || "pending", holdReason: s.holdReason || "" });
                              onUpdateOpen();
                            }}
                          >
                            Status
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="orange"
                            variant="outline"
                            onClick={() => {
                              setSelectedRecord(s);
                              setDeduction({ reason: "", amount: "" });
                              onDeductionOpen();
                            }}
                          >
                            Deduction
                          </Button>
                        </Flex>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 9 : 8} style={{ textAlign: "center", color: "#a0aec0" }}>
                    No salary records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Create Salary Modal */}
        <Modal isOpen={isCreateOpen} onClose={onCreateClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create Salary Record</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={3} isRequired>
                <FormLabel>Employee</FormLabel>
                <Select
                  placeholder="Select Employee"
                  value={newSalary.email}
                  onChange={(e) => setNewSalary({ ...newSalary, email: e.target.value })}
                >
                  {employees.map((emp) => (
                    <option key={emp.value} value={emp.value}>{emp.label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>Month/Year</FormLabel>
                <Select
                  placeholder="Select Month"
                  value={newSalary.month ? `${newSalary.month}-${newSalary.year}` : ""}
                  onChange={(e) => {
                    const [m, y] = e.target.value.split("-");
                    setNewSalary({ ...newSalary, month: m, year: y });
                  }}
                >
                  {generateMonthYearArray().map((el) => (
                    <option key={`${el.month}-${el.year}`} value={`${el.month}-${el.year}`}>
                      {el.text}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>Base Salary</FormLabel>
                <Input
                  type="number"
                  value={newSalary.baseSalary}
                  onChange={(e) => setNewSalary({ ...newSalary, baseSalary: e.target.value })}
                  placeholder="Enter base salary"
                />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Salary Due Date</FormLabel>
                <Input
                  type="date"
                  value={newSalary.salaryDueDate}
                  onChange={(e) => setNewSalary({ ...newSalary, salaryDueDate: e.target.value })}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onCreateClose}>Cancel</Button>
              <Button
                bg="#327171"
                color="white"
                onClick={handleCreateSalary}
                isLoading={isSubmitting}
                _hover={{ bg: "#285e5e" }}
              >
                Create
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Update Status Modal */}
        <Modal isOpen={isUpdateOpen} onClose={onUpdateClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update Salary Status</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedRecord && (
                <>
                  <Text mb={3}>
                    <strong>{selectedRecord.name || selectedRecord.email}</strong> —{" "}
                    {DateTime.fromObject({ month: parseInt(selectedRecord.month) }).toFormat("MMMM")} {selectedRecord.year}
                  </Text>
                  <FormControl mb={3}>
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={updateData.status}
                      onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="on_hold">On Hold</option>
                    </Select>
                  </FormControl>
                  {updateData.status === "on_hold" && (
                    <FormControl mb={3}>
                      <FormLabel>Hold Reason</FormLabel>
                      <Textarea
                        value={updateData.holdReason}
                        onChange={(e) => setUpdateData({ ...updateData, holdReason: e.target.value })}
                        placeholder="Reason for holding salary"
                      />
                    </FormControl>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onUpdateClose}>Cancel</Button>
              <Button bg="#327171" color="white" onClick={handleUpdateStatus} _hover={{ bg: "#285e5e" }}>
                Update
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Add Deduction Modal */}
        <Modal isOpen={isDeductionOpen} onClose={onDeductionClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add Deduction</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedRecord && (
                <Text mb={3}>
                  <strong>{selectedRecord.name || selectedRecord.email}</strong> —{" "}
                  {DateTime.fromObject({ month: parseInt(selectedRecord.month) }).toFormat("MMMM")} {selectedRecord.year}
                </Text>
              )}
              <FormControl mb={3} isRequired>
                <FormLabel>Reason</FormLabel>
                <Input
                  value={deduction.reason}
                  onChange={(e) => setDeduction({ ...deduction, reason: e.target.value })}
                  placeholder="Deduction reason"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>Amount</FormLabel>
                <Input
                  type="number"
                  value={deduction.amount}
                  onChange={(e) => setDeduction({ ...deduction, amount: e.target.value })}
                  placeholder="Amount"
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onDeductionClose}>Cancel</Button>
              <Button bg="#327171" color="white" onClick={handleAddDeduction} _hover={{ bg: "#285e5e" }}>
                Add Deduction
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    </HrmsPageShell>
  );
};
